/**
 * Runtime validation for stored `StoreThemeJson`. Used in three places:
 *
 * 1. Backend `seller/store-theme` service before persisting `draft_*`.
 * 2. Backend `seller/store-theme` service before publishing (defense in
 *    depth — the editor's pre-publish gate runs the same checks).
 * 3. CI snapshot/contract tests.
 *
 * Validation here is structural and constraint-based (does the JSON match
 * the schema shape, are locked sections present, are bilingual fields
 * non-empty in at least one locale, etc). It is NOT SEO validation — that
 * happens in `apps/buyer/src/lib/seo/validateSeoBeforePublish.ts` (Phase 3).
 */
import { z } from "zod";
import { CURRENT_SCHEMA_VERSION, MAX_CUSTOM_PAGES } from "./index";
import { isSectionAvailableOnTemplate, requiredLockedSectionTypes, sectionRegistry, } from "./registry";
import { getProductCardSchema } from "./product-cards";
// ---------------------------------------------------------------------------
// Primitive zod schemas
// ---------------------------------------------------------------------------
const localeStringSchema = z.object({
    ar: z.string().nullable(),
    en: z.string().nullable(),
});
/**
 * Image value: `url` is the only hard requirement. `objectKey` is present
 * for images uploaded through the editor's media library, but externally
 * hosted images (defaults seeded for new stories blocks, sellers pasting
 * hosted URLs, etc.) only carry `url` and would otherwise fail validation
 * even though they render correctly on the storefront.
 */
const imageValueSchema = z.object({
    objectKey: z.string().min(1).optional(),
    url: z.string().url(),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
    altAr: z.string().optional(),
    altEn: z.string().optional(),
    focalX: z.number().min(0).max(100).optional(),
    focalY: z.number().min(0).max(100).optional(),
    fit: z.enum(["cover", "contain"]).optional(),
});
const buttonVariantStyleSchema = z.object({
    background: z.string().optional(),
    text: z.string().optional(),
    radius: z.string().min(1).optional(),
    size: z.enum(["sm", "md", "lg"]).optional(),
    underline: z.boolean().optional(),
});
/** A product-card selection: card id + its card-local settings bag. Mirrors
 *  `ProductCardInstance` in types.ts (kept zod-free); the assertion below
 *  guards the two against drift. */
const productCardInstanceSchema = z.object({
    type: z.string(),
    settings: z.record(z.string(), z.unknown()),
});
const themeTokensSchema = z.object({
    colors: z.object({
        primary: z.string().min(1),
        accent: z.string().min(1),
        surface: z.string().min(1),
        ink: z.string().min(1),
        surfaceSubtle: z.string().min(1).optional(),
        surfaceDark: z.string().min(1).optional(),
        pageGround: z.string().min(1).optional(),
        footerSurface: z.string().min(1).optional(),
        footerInk: z.string().min(1).optional(),
        footerHeading: z.string().min(1).optional(),
        headerSurface: z.string().min(1).optional(),
        headerInk: z.string().min(1).optional(),
    }),
    typography: z.object({
        heading: z.string().min(1),
        body: z.string().min(1),
        headingEn: z.string().min(1).optional(),
        headingAr: z.string().min(1).optional(),
        bodyEn: z.string().min(1).optional(),
        bodyAr: z.string().min(1).optional(),
        displayEn: z.string().min(1).optional(),
        displayAr: z.string().min(1).optional(),
    }),
    radius: z.string().min(1),
    logoTreatment: z.enum(["square", "circle", "none", "wordmark"]),
    bandRadius: z.string().min(1).optional(),
    heroHeight: z.string().min(1).optional(),
    bandHeight: z.string().min(1).optional(),
    pageGutter: z.string().min(1).optional(),
    pageGutterMobile: z.string().min(1).optional(),
    heroBleed: z.boolean().optional(),
    headingRole: z.enum(["ink", "primary", "accent"]).optional(),
    // Structured product-card selection (successor to the flat cluster below).
    productCard: productCardInstanceSchema.optional(),
    productCardStyle: z
        .enum([
        "grid",
        "clean",
        "overlay",
        "compact",
        "card",
        "simple",
        "soft-tile",
        "editorial",
        "centered-accent",
        "rounded-shadow",
        "list-row",
        "bordered",
        "framed",
        "bold-tile",
        "catalog",
        "showcase",
        "gamer",
    ])
        .optional(),
    productCardRadius: z.string().min(1).optional(),
    productCardRadiusInherit: z.boolean().optional(),
    productCardShadow: z.enum(["none", "sm", "md", "lg"]).optional(),
    productCardImageFit: z.enum(["cover", "contain"]).optional(),
    productCardImageBg: z.string().optional(),
    productCardShowColors: z.boolean().optional(),
    productCardShowAddButton: z.boolean().optional(),
    productCardAddButtonStyle: z.enum(["outline", "solid"]).optional(),
    productCardAddButtonRadius: z.string().min(1).optional(),
    productCardAddButtonLabel: localeStringSchema.optional(),
    buttonStyle: z.enum(["solid", "outline", "ghost", "link"]).optional(),
    buttonVariants: z
        .object({
        solid: buttonVariantStyleSchema.optional(),
        outline: buttonVariantStyleSchema.optional(),
        ghost: buttonVariantStyleSchema.optional(),
        link: buttonVariantStyleSchema.optional(),
    })
        .optional(),
    buttonBackground: z.string().optional(),
    buttonTextColor: z.string().optional(),
    buttonRadius: z.string().min(1).optional(),
    buttonSize: z.enum(["sm", "md", "lg"]).optional(),
    // Theme-level scroll-reveal driver (see ThemeTokens). Absent ⇒ off, so
    // existing stores stay static.
    sectionReveal: z.boolean().optional(),
    sectionRevealStyle: z.enum(["uniform", "cycle"]).optional(),
    sectionRevealSpeed: z.number().optional(),
});
/** Instantiating with anything but `true` is a compile error. */
function assertExact() {
    /* type-level only — never invoked at runtime */
}
assertExact();
assertExact();
assertExact();
assertExact();
/** Returns errors and warnings for a single field's value against its schema. */
function validateFieldValue(field, value, basePath) {
    const errors = [];
    const warnings = [];
    const path = `${basePath}.${field.id}`;
    // Conditional fields (showIf) skip validation when their condition is false.
    // The editor enforces this in the UI; here we just allow `undefined`.
    if (value === undefined || value === null) {
        if (field.validate?.required === true) {
            errors.push({
                path,
                code: "required",
                message: `${field.id} is required`,
            });
        }
        return { errors, warnings };
    }
    switch (field.type) {
        case "text":
        case "textarea":
        case "richText": {
            if (field.i18n) {
                const parsed = localeStringSchema.safeParse(value);
                if (!parsed.success) {
                    errors.push({
                        path,
                        code: "invalid_type",
                        message: "Expected { ar, en } object",
                    });
                    break;
                }
                const { ar, en } = parsed.data;
                const both = (ar ?? "").trim().length === 0 && (en ?? "").trim().length === 0;
                // Empty bilingual fields are allowed at draft time but blocked at publish
                // by `validateForPublish`. Here we only enforce length bounds when filled.
                if (field.validate?.min !== undefined &&
                    ar &&
                    ar.length < field.validate.min &&
                    en &&
                    en.length < field.validate.min) {
                    errors.push({
                        path,
                        code: "too_short",
                        message: `Min ${field.validate.min} chars`,
                    });
                }
                if (field.validate?.max !== undefined) {
                    if (ar && ar.length > field.validate.max) {
                        errors.push({
                            path: `${path}.ar`,
                            code: "too_long",
                            message: `Max ${field.validate.max} chars`,
                        });
                    }
                    if (en && en.length > field.validate.max) {
                        errors.push({
                            path: `${path}.en`,
                            code: "too_long",
                            message: `Max ${field.validate.max} chars`,
                        });
                    }
                }
                // Empty bilingual on a `validate.required` field is a warning at
                // draft time and promoted to an error at publish time. Optional
                // bilingual fields are allowed to stay empty — they fall back to
                // SSR-rendered defaults (e.g., hero headline falls back to the
                // store name). Don't surface a warning at all for those.
                if (both && field.validate?.required === true) {
                    warnings.push({
                        path,
                        code: "bilingual_empty",
                        message: `Both locales empty for ${field.id}`,
                    });
                }
            }
            else {
                if (typeof value !== "string") {
                    errors.push({
                        path,
                        code: "invalid_type",
                        message: "Expected string",
                    });
                    break;
                }
                if (field.validate?.min !== undefined &&
                    value.length < field.validate.min) {
                    errors.push({
                        path,
                        code: "too_short",
                        message: `Min ${field.validate.min} chars`,
                    });
                }
                if (field.validate?.max !== undefined &&
                    value.length > field.validate.max) {
                    errors.push({
                        path,
                        code: "too_long",
                        message: `Max ${field.validate.max} chars`,
                    });
                }
            }
            break;
        }
        case "image": {
            const parsed = imageValueSchema.safeParse(value);
            if (!parsed.success) {
                errors.push({
                    path,
                    code: "invalid_image",
                    message: "Image must be { objectKey, url, ... }",
                });
            }
            break;
        }
        case "url": {
            // Accept any of: empty string ("no link"), `#` (anchor placeholder
            // — what sellers paste while still authoring), relative paths
            // (`/collections/foo`), absolute http(s) URLs, and the common
            // protocols `mailto:` / `tel:`. The schema's `validate.required`
            // already controls whether a URL is mandatory; this case only
            // rejects clearly malformed strings.
            if (typeof value !== "string") {
                errors.push({
                    path,
                    code: "invalid_url",
                    message: "Expected URL string",
                });
                break;
            }
            const trimmed = value.trim();
            const isAllowed = trimmed.length === 0 ||
                trimmed === "#" ||
                trimmed.startsWith("/") ||
                /^https?:\/\//i.test(trimmed) ||
                /^mailto:/i.test(trimmed) ||
                /^tel:/i.test(trimmed);
            if (!isAllowed) {
                errors.push({
                    path,
                    code: "invalid_url",
                    message: "Expected URL, relative path, mailto:, or tel:",
                });
            }
            break;
        }
        case "color": {
            // Empty string === "no override; fall back to the theme token". The
            // editor's color picker clears a value this way when the seller
            // unsets a per-section background/text override, so empty must be
            // accepted at validation time.
            if (typeof value !== "string") {
                errors.push({
                    path,
                    code: "invalid_color",
                    message: "Expected color string",
                });
            }
            break;
        }
        case "colorScheme": {
            if (value !== "light" && value !== "dark" && value !== "auto") {
                errors.push({
                    path,
                    code: "invalid_colorScheme",
                    message: "Expected light|dark|auto",
                });
            }
            break;
        }
        case "select": {
            const allowed = field.options.map((o) => o.value);
            if (typeof value !== "string" || !allowed.includes(value)) {
                errors.push({
                    path,
                    code: "invalid_option",
                    message: `Expected one of ${allowed.join(",")}`,
                });
            }
            break;
        }
        case "range": {
            if (typeof value !== "number") {
                errors.push({ path, code: "invalid_type", message: "Expected number" });
                break;
            }
            if (value < field.min || value > field.max) {
                errors.push({
                    path,
                    code: "out_of_range",
                    message: `Must be ${field.min}–${field.max}`,
                });
            }
            break;
        }
        case "checkbox": {
            if (typeof value !== "boolean") {
                errors.push({
                    path,
                    code: "invalid_type",
                    message: "Expected boolean",
                });
            }
            break;
        }
        case "datetime": {
            // Stored as a `datetime-local` string (`YYYY-MM-DDTHH:mm`). Empty is
            // allowed (no target set yet); when filled it must be a parseable date.
            if (typeof value !== "string") {
                errors.push({
                    path,
                    code: "invalid_type",
                    message: "Expected datetime string",
                });
                break;
            }
            if (value.trim().length > 0 && Number.isNaN(Date.parse(value))) {
                errors.push({
                    path,
                    code: "invalid_datetime",
                    message: "Expected a valid date-time",
                });
            }
            break;
        }
        case "productPicker":
        case "categoryPicker": {
            // Single-mode pickers store a string id; multi-mode store an array.
            if (field.type === "categoryPicker" && field.single === true) {
                if (typeof value !== "string") {
                    errors.push({
                        path,
                        code: "invalid_type",
                        message: "Expected string",
                    });
                }
                break;
            }
            if (!Array.isArray(value)) {
                errors.push({ path, code: "invalid_type", message: "Expected array" });
                break;
            }
            if (field.maxItems !== undefined && value.length > field.maxItems) {
                errors.push({
                    path,
                    code: "too_many",
                    message: `Max ${field.maxItems} items`,
                });
            }
            for (const id of value) {
                if (typeof id !== "string") {
                    errors.push({
                        path,
                        code: "invalid_id",
                        message: "Each id must be string",
                    });
                    break;
                }
            }
            break;
        }
        case "headingLevel": {
            if (![1, 2, 3, 4].includes(value)) {
                errors.push({
                    path,
                    code: "invalid_heading",
                    message: "Expected 1|2|3|4",
                });
            }
            break;
        }
        default: {
            // Exhaustiveness guard — typescript catches missing cases at compile.
            const _exhaustive = field;
            errors.push({
                path,
                code: "unknown_field_type",
                message: `Unknown field: ${JSON.stringify(_exhaustive)}`,
            });
        }
    }
    return { errors, warnings };
}
function validateSection(section, basePath, template) {
    const errors = [];
    const warnings = [];
    const schema = sectionRegistry.sections[section.type];
    if (!schema) {
        errors.push({
            path: basePath,
            code: "unknown_section",
            message: `Unknown section type: ${section.type}`,
        });
        return { errors, warnings };
    }
    // Section type must be allowed on this template (e.g., a `hero` cannot
    // appear on the product template, a `product-details` cannot appear on
    // home). Catches programming errors and DB tampering.
    if (!isSectionAvailableOnTemplate(section.type, template)) {
        errors.push({
            path: basePath,
            code: "section_not_allowed_on_template",
            message: `Section "${section.type}" is not available on template "${template}"`,
        });
    }
    // Locked-flag must match schema. Prevents flipping `locked` via DB tampering.
    if (Boolean(schema.locked) !== Boolean(section.locked)) {
        errors.push({
            path: `${basePath}.locked`,
            code: "locked_mismatch",
            message: `Section ${section.type} locked flag must be ${Boolean(schema.locked)}`,
        });
    }
    // Critical-for-SEO sections cannot be hidden.
    if (schema.criticalForSeo && section.visible === false) {
        errors.push({
            path: `${basePath}.visible`,
            code: "cannot_hide_critical",
            message: `Section ${section.type} is critical for SEO and cannot be hidden`,
        });
    }
    // Validate each declared field.
    for (const field of schema.settings) {
        const out = validateFieldValue(field, section.settings[field.id], `${basePath}.settings`);
        errors.push(...out.errors);
        warnings.push(...out.warnings);
    }
    // Validate blocks if any.
    if (section.blocks && schema.blocks) {
        for (const [i, block] of section.blocks.entries()) {
            const blockSchema = schema.blocks.find((b) => b.type === block.type);
            if (!blockSchema) {
                errors.push({
                    path: `${basePath}.blocks[${i}]`,
                    code: "unknown_block",
                    message: `Unknown block type ${block.type} in section ${section.type}`,
                });
                continue;
            }
            for (const field of blockSchema.settings) {
                const out = validateFieldValue(field, block.settings[field.id], `${basePath}.blocks[${i}].settings`);
                errors.push(...out.errors);
                warnings.push(...out.warnings);
            }
        }
    }
    return { errors, warnings };
}
function validateTemplate(template, basePath, templateName) {
    const errors = [];
    const warnings = [];
    // Locked sections required ON THIS TEMPLATE must be present exactly once.
    // (e.g., `hero` is locked on home but doesn't exist on product/collection.)
    const presentTypes = new Set(template.sections.map((s) => s.type));
    for (const required of requiredLockedSectionTypes(templateName)) {
        if (!presentTypes.has(required)) {
            errors.push({
                path: basePath,
                code: "missing_locked",
                message: `Locked section "${required}" must be present on template "${templateName}"`,
            });
        }
    }
    // Header must be first; footer must be last (universal rule across templates).
    if (template.sections[0]?.type !== "header") {
        errors.push({
            path: `${basePath}.sections[0]`,
            code: "header_position",
            message: "Header must be the first section",
        });
    }
    if (template.sections.at(-1)?.type !== "footer") {
        errors.push({
            path: `${basePath}.sections[last]`,
            code: "footer_position",
            message: "Footer must be the last section",
        });
    }
    // maxPerTemplate enforcement.
    const counts = {};
    for (const section of template.sections) {
        counts[section.type] = (counts[section.type] ?? 0) + 1;
    }
    for (const [type, count] of Object.entries(counts)) {
        const max = sectionRegistry.sections[type]?.maxPerTemplate;
        if (max !== undefined && count > max) {
            errors.push({
                path: basePath,
                code: "too_many_sections",
                message: `Section ${type}: ${count} instances, max ${max}`,
            });
        }
    }
    // Per-section validation. Pass the template name so misplaced sections
    // (e.g., a hero added to the product template) are caught.
    for (const [i, section] of template.sections.entries()) {
        const out = validateSection(section, `${basePath}.sections[${i}]`, templateName);
        errors.push(...out.errors);
        warnings.push(...out.warnings);
    }
    return { errors, warnings };
}
/**
 * Validate a custom (seller-authored) page. Custom pages don't carry
 * platform-locked sections like `hero` or `product-details`; they're
 * universal-only. The header/footer are still rendered by the buyer
 * shell so we don't require those in the page's own sections.
 */
function validateCustomPage(page, basePath) {
    const errors = [];
    const warnings = [];
    // Slug shape — lowercase letters/digits/hyphens, 1–60 chars.
    if (!/^[a-z0-9][a-z0-9-]{0,59}$/.test(page.slug)) {
        errors.push({
            path: `${basePath}.slug`,
            code: "invalid_slug",
            message: `Slug must be lowercase letters / digits / hyphens (1–60 chars). Got "${page.slug}".`,
        });
    }
    // Bilingual name — at least one locale must be filled at publish time.
    // Draft mode tolerates empty (warning only) so the editor can persist
    // the page while the seller is still authoring.
    const hasName = (page.name?.ar?.trim().length ?? 0) > 0 ||
        (page.name?.en?.trim().length ?? 0) > 0;
    if (!hasName) {
        warnings.push({
            path: `${basePath}.name`,
            code: "bilingual_empty",
            message: `Page name is empty in both locales`,
        });
    }
    // Per-section validation — only universal sections (banner, about, faq)
    // may live on a custom page. Locked / template-restricted sections
    // (hero, product-details, …) are rejected.
    for (const [i, section] of page.sections.entries()) {
        const schema = sectionRegistry.sections[section.type];
        if (!schema) {
            errors.push({
                path: `${basePath}.sections[${i}]`,
                code: "unknown_section",
                message: `Unknown section type: ${section.type}`,
            });
            continue;
        }
        if (schema.locked) {
            errors.push({
                path: `${basePath}.sections[${i}]`,
                code: "locked_section_on_custom_page",
                message: `Locked section "${section.type}" cannot be added to a custom page`,
            });
            continue;
        }
        const allowed = schema.availableOnTemplates;
        if (allowed && allowed.length > 0) {
            // Template-restricted section (e.g. hero is home-only) — reject on
            // custom pages. Universal sections leave `availableOnTemplates`
            // undefined.
            errors.push({
                path: `${basePath}.sections[${i}]`,
                code: "section_not_allowed_on_custom_page",
                message: `Section "${section.type}" is not available on custom pages`,
            });
            continue;
        }
        // Field-level validation reuses the section validator. We pass
        // `'home'` as the template arg because the per-template availability
        // is already enforced above; the field-level pass is template-blind.
        const out = validateSection(section, `${basePath}.sections[${i}]`, "home");
        errors.push(...out.errors);
        warnings.push(...out.warnings);
    }
    return { errors, warnings };
}
// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
/**
 * Validate a `StoreThemeJson` structurally.
 *
 * - In `'draft'` mode (default), accepts content with empty bilingual fields
 *   — those are reported in `warnings` for the editor to surface as soft hints.
 * - In `'publish'` mode, empty bilingual fields are promoted to errors.
 *
 * Always returns warnings even when `ok: true`, so editor UX can show hints.
 */
/**
 * Validate a structured `productCard` selection against the product-card
 * registry: the card id must be a real card, and each provided setting must
 * satisfy that card's field schema. Mirrors the section-settings validation so
 * a bogus card id or out-of-range setting is rejected before publish — the
 * exact failure mode the ProductCardSchema registry exists to prevent
 * (docs/plans/2026-07-17-composable-themes.md §8 S2).
 */
function validateProductCardValue(productCard, basePath) {
    const errors = [];
    const warnings = [];
    const schema = getProductCardSchema(productCard.type);
    if (!schema) {
        errors.push({
            path: `${basePath}.type`,
            code: "unknown_product_card",
            message: `Unknown product-card type: ${productCard.type}`,
        });
        return { errors, warnings };
    }
    for (const field of schema.settings) {
        const out = validateFieldValue(field, productCard.settings[field.id], `${basePath}.settings`);
        errors.push(...out.errors);
        warnings.push(...out.warnings);
    }
    return { errors, warnings };
}
export function validateThemeJson(input, options = {}) {
    const mode = options.mode ?? "draft";
    const errors = [];
    const warnings = [];
    const shapeOk = z
        .object({
        schemaVersion: z.number().int().positive(),
        settings: themeTokensSchema,
        templates: z.record(z.string(), z.object({ sections: z.array(z.any()) })),
        pages: z
            .record(z.string(), z.object({
            id: z.string().min(1),
            slug: z.string().min(1),
            name: localeStringSchema,
            sections: z.array(z.any()),
        }))
            .optional(),
    })
        .safeParse(input);
    if (!shapeOk.success) {
        return {
            ok: false,
            errors: shapeOk.error.issues.map((issue) => ({
                path: issue.path.join("."),
                code: issue.code,
                message: issue.message,
            })),
            warnings: [],
        };
    }
    const data = shapeOk.data;
    // Normalize each section's `locked` flag to its schema's value (schema is
    // authoritative). Heals themes saved while a type was locked but since
    // unlocked — without this they'd fail the `locked_mismatch` check below.
    const normalizeLocked = (sections) => {
        for (const s of sections) {
            const schema = sectionRegistry.sections[s.type];
            if (!schema)
                continue;
            if (schema.locked)
                s.locked = true;
            else if ("locked" in s)
                delete s.locked;
        }
    };
    for (const tpl of Object.values(data.templates)) {
        if (tpl)
            normalizeLocked(tpl.sections);
    }
    if (data.pages) {
        for (const pg of Object.values(data.pages)) {
            if (pg)
                normalizeLocked(pg.sections);
        }
    }
    if (data.schemaVersion > CURRENT_SCHEMA_VERSION) {
        errors.push({
            path: "schemaVersion",
            code: "schema_too_new",
            message: `schemaVersion ${data.schemaVersion} is newer than this build's ${CURRENT_SCHEMA_VERSION}. Upgrade the schemas package.`,
        });
    }
    // Validate the structured product-card selection against the registry (a
    // typo'd card id or bad setting must not reach a live store).
    if (data.settings.productCard) {
        const out = validateProductCardValue(data.settings.productCard, "settings.productCard");
        errors.push(...out.errors);
        warnings.push(...out.warnings);
    }
    // Draft mode is forgiving: silently strip any section types that no
    // longer exist in the registry (e.g. a section type was removed in a
    // schema refactor and the seller's saved draft still has instances of
    // it). The drop is recorded as a warning so we can spot it in logs but
    // doesn't block the save. Publish mode keeps the strict path below — a
    // section that can't be rendered should never make it to live.
    if (mode === "draft") {
        for (const [name, template] of Object.entries(data.templates)) {
            if (!template)
                continue;
            template.sections = template.sections.filter((s) => {
                const ok = !!sectionRegistry.sections[s.type];
                if (!ok) {
                    warnings.push({
                        path: `templates.sections`,
                        code: "stripped_unknown_section",
                        message: `Stripped unknown section type "${s.type}" (id="${s.id}")`,
                    });
                }
                return ok;
            });
            // Auto-insert any required locked section that's missing. This keeps
            // older drafts compatible when we add a new locked section type
            // (e.g. `top-seller`) — the next save quietly heals the draft
            // instead of bouncing it with a missing_locked error.
            const presentTypes = new Set(template.sections.map((s) => s.type));
            for (const requiredType of requiredLockedSectionTypes(name)) {
                if (presentTypes.has(requiredType))
                    continue;
                const stub = {
                    id: `${requiredType}-1`,
                    type: requiredType,
                    locked: true,
                    visible: true,
                    settings: {},
                };
                // Insert before the footer if footer is present (footer is always
                // last by convention); otherwise append.
                const footerIdx = template.sections.findIndex((s) => s.type === "footer");
                if (footerIdx >= 0) {
                    template.sections.splice(footerIdx, 0, stub);
                }
                else {
                    template.sections.push(stub);
                }
                warnings.push({
                    path: `templates.${name}.sections`,
                    code: "inserted_missing_locked",
                    message: `Auto-inserted missing locked section "${requiredType}"`,
                });
            }
        }
        if (data.pages) {
            for (const page of Object.values(data.pages)) {
                if (!page)
                    continue;
                page.sections = page.sections.filter((s) => {
                    const ok = !!sectionRegistry.sections[s.type];
                    if (!ok) {
                        warnings.push({
                            path: `pages.${page.id}.sections`,
                            code: "stripped_unknown_section",
                            message: `Stripped unknown section type "${s.type}" (id="${s.id}")`,
                        });
                    }
                    return ok;
                });
            }
        }
    }
    for (const [name, template] of Object.entries(data.templates)) {
        if (!template)
            continue;
        const out = validateTemplate(template, `templates.${name}`, name);
        errors.push(...out.errors);
        warnings.push(...out.warnings);
    }
    // Custom pages — same per-section validation as templates, plus a few
    // extra rules: cap, unique slugs, only universal sections.
    if (data.pages) {
        const pageEntries = Object.entries(data.pages);
        if (pageEntries.length > MAX_CUSTOM_PAGES) {
            errors.push({
                path: "pages",
                code: "too_many_pages",
                message: `At most ${MAX_CUSTOM_PAGES} custom pages allowed; found ${pageEntries.length}`,
            });
        }
        const seenSlugs = new Set();
        for (const [pageId, page] of pageEntries) {
            if (!page)
                continue;
            const out = validateCustomPage(page, `pages.${pageId}`);
            errors.push(...out.errors);
            warnings.push(...out.warnings);
            if (page.slug) {
                if (seenSlugs.has(page.slug)) {
                    errors.push({
                        path: `pages.${pageId}.slug`,
                        code: "duplicate_slug",
                        message: `Slug "${page.slug}" is used by another page`,
                    });
                }
                seenSlugs.add(page.slug);
            }
        }
    }
    // In publish mode, promote bilingual_empty warnings to errors.
    if (mode === "publish") {
        // A publishable theme MUST have a home template with content. An empty
        // `templates: {}` trivially satisfies the record shape check above, so
        // without this guard a wiped theme (e.g. a hand-edited JSON mistake in the
        // admin editor) could be published and render blank for every store that
        // applies it. See docs/plans/2026-07-17-composable-themes.md §8 (S1/S7).
        const home = data.templates.home;
        if (!home || home.sections.length === 0) {
            errors.push({
                path: "templates.home",
                code: "missing_home_template",
                message: "A publishable theme must have a home template with at least one section.",
            });
        }
        const promoted = warnings.filter((w) => w.code === "bilingual_empty");
        errors.push(...promoted);
        const remaining = warnings.filter((w) => w.code !== "bilingual_empty");
        if (errors.length > 0)
            return { ok: false, errors, warnings: remaining };
        return { ok: true, data, warnings: remaining };
    }
    if (errors.length > 0)
        return { ok: false, errors, warnings };
    return { ok: true, data, warnings };
}
/** Validate a single section instance. Used by the editor for live feedback. */
export function validateSectionInstance(section, path = "section", template = "home") {
    return validateSection(section, path, template);
}
//# sourceMappingURL=validate.js.map